import { supabase } from "@/integrations/supabase/client";
import { handleSupabaseFunctionError } from "@/utils/supabaseFunctionsError";
import type {
  BulkInsertRiskAnalysisRequest,
  BulkInsertRiskAnalysisResponse,
  LoadDefaultMatrixParams,
  LoadDefaultMatrixResponse,
  LoadMatrixDataParams,
  LoadMatrixDataResponse,
  LoadRiskMatrixResponse,
  MatrixDataResponse,
  RiskAnalysisById,
  RiskAnalysisByIdResponse,
  RiskAnalysisBySession,
  RiskAnalysisBySessionResponse,
  RiskAnalysisHistoryItem,
  RiskAnalysisHistoryResponse,
  RiskAnalysisRecord,
  RiskMatrixData,
  SaveJustificationsRequest,
  SaveJustificationsResponse,
  SaveRiskAnalysisMatrixRequest,
  SaveRiskAnalysisMatrixResponse,
} from "@/types/risk-analysis";
import {
  BulkDeleteRiskAnalysisRequest,
  BulkDeleteRiskAnalysisResponse,
  DeleteSingleRiskAnalysisRequest,
  DeleteSingleRiskAnalysisResponse,
} from "../types/riskAnalysis";

export interface LoadRiskMatrixParams {
  session_id: string;
  sector_id?: string;
}

/**
 * Load risk analysis data from the risk_analysis table
 *
 * @param params - Query parameters (session_id required, sector_id optional)
 * @returns Risk analysis data (single record if sector_id provided, array otherwise)
 */
export async function loadRiskMatrixData(
  params: LoadRiskMatrixParams
): Promise<RiskAnalysisRecord | RiskAnalysisRecord[]> {
  const queryParams = new URLSearchParams({
    session_id: params.session_id,
  });

  if (params.sector_id) {
    queryParams.append("sector_id", params.sector_id);
  }

  const { data, error } = await supabase.functions.invoke(
    `api/load-data-risk-matrix?${queryParams.toString()}`,
    {
      method: "GET",
    }
  );

  await handleSupabaseFunctionError(error);

  // The API returns { success, data, count }
  const response = data as LoadRiskMatrixResponse;

  if (!response.success) {
    throw new Error(response.error || "Failed to load risk matrix data");
  }

  return response.data;
}

/**
 * Load matrix data for a specific session and sector
 *
 * @param params - Query parameters (session_id and sector_id both required)
 * @returns Matrix data with metadata
 */
export async function loadMatrixData(
  params: LoadMatrixDataParams
): Promise<MatrixDataResponse> {
  const queryParams = new URLSearchParams({
    session_id: params.session_id,
    sector_id: params.sector_id,
  });

  const { data, error } = await supabase.functions.invoke(
    `api/load-data-risk-matrix?${queryParams.toString()}`,
    {
      method: "GET",
    }
  );

  await handleSupabaseFunctionError(error);

  // The API returns { success, data }
  const response = data as LoadMatrixDataResponse;

  if (!response.success) {
    throw new Error(response.error || "Failed to load matrix data");
  }

  if (!response.data) {
    throw new Error("No matrix data returned from API");
  }

  return response.data;
}

/**
 * Load default matrix data for a specific session and sector
 *
 * @param params - Query parameters (session_id and sector_id both required)
 * @returns Default matrix data structure
 */
export async function loadDefaultMatrix(
  params: LoadDefaultMatrixParams
): Promise<RiskMatrixData> {
  const queryParams = new URLSearchParams({
    session_id: params.session_id,
    sector_id: params.sector_id,
  });

  const { data, error } = await supabase.functions.invoke(
    `api/load-default-matrix?${queryParams.toString()}`,
    {
      method: "GET",
    }
  );

  await handleSupabaseFunctionError(error);

  // The API returns { success, data }
  const response = data as LoadDefaultMatrixResponse;

  if (!response.success) {
    throw new Error(response.error || "Failed to load default matrix data");
  }

  if (!response.data) {
    throw new Error("No default matrix data returned from API");
  }

  return response.data as RiskMatrixData;
}

export async function bulkInsertRiskAnalysis(
  request: BulkInsertRiskAnalysisRequest
): Promise<BulkInsertRiskAnalysisResponse> {
  try {
    const { records } = request;

    if (!records || records.length === 0) {
      return {
        success: false,
        error: "No records provided for insertion",
      };
    }

    // Use upsert to handle the UNIQUE constraint on (session_id, sector_id)
    const { data: insertedRecords, error: insertError } = await supabase
      .from("risk_analysis")
      .upsert(records, {
        onConflict: "session_id,sector_id",
      })
      .select();

    if (insertError) {
      console.error(
        "❌ [RiskAnalysis] Error bulk inserting risk analysis:",
        insertError
      );
      return {
        success: false,
        error:
          insertError.message || "Failed to bulk insert risk analysis records",
      };
    }

    console.log(
      `✅ [RiskAnalysis] Successfully inserted ${
        insertedRecords?.length || 0
      } risk analysis records`
    );

    return {
      success: true,
      data: (insertedRecords || []) as unknown as RiskAnalysisRecord[],
      count: insertedRecords?.length || 0,
    };
  } catch (error) {
    console.error("❌ [RiskAnalysis] Unexpected error:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error occurred",
    };
  }
}

/**
 * Get risk analysis history for the current user
 *
 * @returns Array of risk analysis history items
 */
export async function getRiskAnalysisHistory(): Promise<
  RiskAnalysisHistoryItem[]
> {
  const { data, error } = await supabase.functions.invoke(
    "api/risk-analysis-history",
    {
      method: "GET",
    }
  );

  await handleSupabaseFunctionError(error);

  const response = data as RiskAnalysisHistoryResponse;

  if (!response.success) {
    throw new Error(response.error || "Failed to load risk analysis history");
  }

  return response.data || [];
}

/**
 * Get all risk analyses for a specific session
 *
 * @param sessionId - UUID of the session
 * @returns Complete risk analysis data for the session
 */
export async function getRiskAnalysisBySession(
  sessionId: string
): Promise<RiskAnalysisBySession> {
  const { data, error } = await supabase.functions.invoke(
    `api/risk-analysis/${sessionId}`,
    {
      method: "GET",
    }
  );

  await handleSupabaseFunctionError(error);

  const response = data as RiskAnalysisBySessionResponse;

  if (!response.success) {
    throw new Error(
      response.error || "Failed to load risk analysis for session"
    );
  }

  if (!response.data) {
    throw new Error("No data returned from API");
  }

  return response.data;
}

export async function getRiskAnalysisById(
  id: string
): Promise<RiskAnalysisById> {
  const { data, error } = await supabase.functions.invoke(
    `api/risk-analysis-id/${id}`,
    {
      method: "GET",
    }
  );

  await handleSupabaseFunctionError(error);

  const response = data as RiskAnalysisByIdResponse;

  if (!response.success) {
    throw new Error(response.error || "Failed to load risk analysis");
  }

  if (!response.data) {
    throw new Error("No data returned from API");
  }

  return response.data;
}

export async function deleteSingleRiskAnalysis(
  request: DeleteSingleRiskAnalysisRequest
): Promise<DeleteSingleRiskAnalysisResponse> {
  const { id } = request;

  const { error } = await supabase.from("risk_analysis").delete().eq("id", id);

  if (error) {
    throw new Error(error.message || "Failed to delete risk analysis record");
  }

  return { success: true };
}

export async function bulkDeleteRiskAnalysis(
  request: BulkDeleteRiskAnalysisRequest
): Promise<BulkDeleteRiskAnalysisResponse> {
  try {
    const { ids } = request;

    if (!ids || ids.length === 0) {
      return {
        success: false,
        error: "No IDs provided for deletion",
      };
    }

    const { data: deletedRecords, error: deleteError } = await supabase
      .from("risk_analysis")
      .delete()
      .in("id", ids)
      .select();

    if (deleteError) {
      console.error(
        "❌ [RiskAnalysis] Error bulk deleting risk analysis:",
        deleteError
      );
      return {
        success: false,
        error:
          deleteError.message || "Failed to bulk delete risk analysis records",
      };
    }

    console.log(
      `✅ [RiskAnalysis] Successfully deleted ${
        deletedRecords?.length || 0
      } risk analysis records`
    );

    return {
      success: true,
      count: deletedRecords?.length || 0,
    };
  } catch (error) {
    console.error("❌ [RiskAnalysis] Unexpected error:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error occurred",
    };
  }
}

export async function saveJustifications(
  request: SaveJustificationsRequest
): Promise<SaveJustificationsResponse> {
  try {
    const { data, error } = await supabase.functions.invoke(
      "api/save-justifications",
      {
        method: "POST",
        body: request,
      }
    );

    await handleSupabaseFunctionError(error);

    const response = data as SaveJustificationsResponse;

    if (!response.success) {
      return {
        success: false,
        error: response.error || "Failed to save justifications",
      };
    }

    return response;
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error occurred",
    };
  }
}

/**
 * Save risk analysis matrix for a single sector
 * Uses backend API endpoint with upsert strategy
 */
export async function saveRiskAnalysisMatrix(
  request: SaveRiskAnalysisMatrixRequest
): Promise<SaveRiskAnalysisMatrixResponse> {
  console.log("🚀 ~ saveRiskAnalysisMatrix ~ request:", request.matrix);
  try {
    const { data, error } = await supabase.functions.invoke("api/save-matrix", {
      method: "POST",
      body: request,
    });

    await handleSupabaseFunctionError(error);

    const response = data as SaveRiskAnalysisMatrixResponse;

    if (!response.success) {
      console.error("❌ [RiskAnalysis] Error saving matrix:", response.error);
      return {
        success: false,
        error: response.error || "Failed to save risk analysis matrix",
      };
    }

    console.log(
      `✅ [RiskAnalysis] Successfully saved matrix for sector ${request.sector_id}`
    );

    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    console.error("❌ [RiskAnalysis] Unexpected error:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error occurred",
    };
  }
}
